import { Modal, ModalButtons } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { useParams } from 'react-router-dom'
import { useInfiniteOrders } from 'api/Orders'
import { useUpdateAssociatedBarcode } from 'api/Customers'
import { useTranslation } from 'react-i18next'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Select } from 'ui/components/Form/Select'
import { Form, Formik } from 'formik'
import { useMemo, useState } from 'react'

interface AssociateOrderModalProps {
  isOpen: boolean
  onClose: () => void
  barcodeId: string
  initialOrderId?: string
}

interface FormValues {
  order_id: string
}

export const AssociateOrderModal = ({
  isOpen,
  onClose,
  barcodeId,
  initialOrderId,
}: AssociateOrderModalProps) => {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const notification = useNotificationContext()
  const [searchQuery, setSearchQuery] = useState('')

  const {
    data: ordersData,
    isLoading: isLoadingOrders,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteOrders(
    {
      query: searchQuery,
    },
    {
      enabled: isOpen,
    }
  )

  const { mutate: updateAssociatedBarcode, isLoading: isUpdating } = useUpdateAssociatedBarcode()

  const orderOptions = useMemo(() => {
    if (!ordersData?.pages) return []

    return ordersData.pages.flatMap((page) =>
      page.data
        .filter((o) => o.order_ref)
        .map((order) => ({
          value: order.id,
          name: order.order_ref,
          description: `${order.first_name} ${order.last_name}`,
        }))
    )
  }, [ordersData])

  const handleAssociate = (vals: FormValues) => {
    updateAssociatedBarcode(
      {
        customerId: clientId,
        barcodeId,
        updateAssociatedBarcode: {
          order_id: vals.order_id || null,
        },
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('clients.client_profile.associate_order_modal.notification.title')}
              description={t(
                'clients.client_profile.associate_order_modal.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
          onClose()
        },
        onError: (response) => {
          notification.notifyApiError(response)
        },
      }
    )
  }

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('clients.client_profile.associate_order_modal.title')}
      onClose={onClose}
      size="sm"
    >
      <Formik initialValues={{ order_id: initialOrderId || '' }} onSubmit={handleAssociate}>
        {({ values, setFieldValue }) => (
          <Form>
            <div className="mb-4">
              <Select
                label={t('clients.client_profile.associate_order_modal.select')}
                options={orderOptions || []}
                selected={orderOptions?.find((opt) => opt.value === values.order_id) || null}
                onChange={(option) => setFieldValue('order_id', option?.value || '')}
                disabled={isLoadingOrders}
                filterEnabled={true}
                onFilterChange={(query) => setSearchQuery(query)}
                loadMore={handleLoadMore}
                allowClear={true}
                isLoadingMore={isLoadingOrders || isFetchingNextPage}
                placeholder={t('clients.client_profile.associate_order_modal.placeholder')}
              />
            </div>

            <ModalButtons>
              <Button
                label={t('clients.client_profile.associate_order_modal.associate_button')}
                variant="primary"
                type="submit"
                disabled={isUpdating}
                loading={isUpdating}
              />
              <Button label={t('modal_button.cancel')} onClick={onClose} />
            </ModalButtons>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
